import { useEffect, useState } from "react";
import { Rocket } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, errText, imgUrl } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

export const BoostModal = ({ itemId, extend = false, trigger, onDone }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/ads/boost/options/${itemId}`).then(({ data }) => {
      setData(data);
      const pop = data.packages.find((p) => p.popular);
      setDays(pop ? pop.days : data.packages[0]?.days);
    }).catch((e) => { toast.error(errText(e)); setOpen(false); });
  }, [open, itemId]);

  const buy = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/ads/boost/checkout", {
        item_id: itemId, days, origin_url: window.location.origin, extend,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(errText(e));
      setBusy(false);
    }
  };

  const selected = data?.packages.find((p) => p.days === days);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md rounded-t-2xl sm:rounded-2xl" data-testid="boost-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-accent" /> {extend ? "Boost verlängern" : "Artikel boosten"}
          </DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="h-40 bg-muted rounded-xl animate-pulse" />
        ) : (
          <div>
            <div className="flex gap-3 items-center">
              {data.item.image_ids?.[0] && <img src={imgUrl(data.item.image_ids[0], "thumb")} alt="" className="w-14 h-14 rounded-xl object-cover border border-border" />}
              <div className="min-w-0">
                <p className="text-sm font-semibold line-clamp-1">{data.item.title}</p>
                <p className="text-sm font-bold">{formatPrice(data.item.price)}</p>
              </div>
            </div>
            <p className="text-xs font-semibold mt-4 mb-2">Wähle deine Laufzeit:</p>
            <div className="space-y-2">
              {data.packages.map((p) => (
                <label key={p.days} data-testid={`boost-pkg-${p.days}`}
                  className={`flex items-center justify-between border rounded-xl px-4 py-3 cursor-pointer transition-colors ${days === p.days ? "border-accent bg-accent/5" : "border-border hover:border-foreground/30"}`}>
                  <span className="flex items-center gap-3 text-sm font-medium">
                    <input type="radio" name="boostdays" className="accent-[#FF6B35]" checked={days === p.days} onChange={() => setDays(p.days)} />
                    {p.days} Tage
                    {p.popular && <span className="text-[10px] font-bold bg-accent text-white px-2 py-0.5 rounded-full">BELIEBT</span>}
                  </span>
                  <span className="text-sm font-bold">{formatPrice(p.price)}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Dein Artikel erhält zusätzliche Sichtbarkeit auf KLEDIO — im Home-Feed, in Kategorien und passenden Suchergebnissen. Ein Boost erhöht die Sichtbarkeit, garantiert aber keine feste Platzierung.
            </p>
            <button data-testid="boost-buy-btn" onClick={buy} disabled={busy || !days}
              className="mt-4 w-full bg-accent text-white font-bold py-3.5 rounded-full hover:opacity-90 active:scale-[0.98] transition-transform disabled:opacity-50">
              {busy ? "Weiterleitung zur Zahlung…" : `${extend ? "Verlängerung" : "Boost"} kaufen · ${selected ? formatPrice(selected.price) : ""}`}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export function boostRemaining(endAt) {
  const ms = new Date(endAt) - Date.now();
  if (ms <= 0) return "abgelaufen";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return d > 0 ? `${d} Tage ${h} Std.` : `${h} Std.`;
}
